import { useCallback, useState } from 'react'
import type { ClassifyTuple } from '@/services/classification.service'
import { REQUIRED_DIMENSIONS } from './classify-config'
import {
	buildCommitTuples,
	type ClassificationTuple,
	effectiveClassification,
	type Readiness,
	readiness,
} from './staging'

/**
 * Owns the classification working set: a group-wide batch tuple plus per-file
 * overrides, keyed by file uuid. Resolution + readiness delegate to the pure
 * helpers in `staging.ts` (unit-tested without rendering).
 */
export function useClassifyStaging(required: string[] = REQUIRED_DIMENSIONS) {
	const [batch, setBatch] = useState<ClassificationTuple>({})
	const [overrides, setOverrides] = useState<Record<string, ClassificationTuple>>({})

	const setBatchValue = useCallback((axisCode: string, valueCode: string) => {
		setBatch((prev) => ({ ...prev, [axisCode]: valueCode }))
	}, [])

	const setRowOverride = useCallback((fileId: string, axisCode: string, valueCode: string) => {
		setOverrides((prev) => ({ ...prev, [fileId]: { ...prev[fileId], [axisCode]: valueCode } }))
	}, [])

	/** Forget a committed file's overrides so its row can be cleared. */
	const clearRows = useCallback((fileIds: string[]) => {
		setOverrides((prev) => {
			const next = { ...prev }
			for (const id of fileIds) delete next[id]
			return next
		})
	}, [])

	const resolve = useCallback(
		(fileId: string): ClassificationTuple => effectiveClassification(batch, overrides[fileId]),
		[batch, overrides],
	)

	const readinessOf = useCallback(
		(fileId: string): Readiness => readiness(resolve(fileId), required),
		[resolve, required],
	)

	/** Tuples for the ready subset of `fileIds`, ready for a single batch call. */
	const commitTuples = useCallback(
		(fileIds: string[]): ClassifyTuple[] =>
			buildCommitTuples(
				fileIds.filter((id) => readinessOf(id).ready).map((fileId) => ({ fileId, classification: resolve(fileId) })),
			),
		[readinessOf, resolve],
	)

	return {
		batch,
		overrides,
		setBatchValue,
		setRowOverride,
		clearRows,
		effectiveClassification: resolve,
		readiness: readinessOf,
		commitTuples,
	}
}
